"use client";

import { useState, useRef, useCallback } from "react";
import {
  IMAGE_UPLOAD_ACCEPT,
  IMAGE_UPLOAD_TIMEOUT_MESSAGE,
  IMAGE_UPLOAD_TIMEOUT_MS,
  isLikelyImageUpload,
} from "@/lib/images/accepted-image-types";

interface AnalysisResult {
  description: string;
  detectedHazards: string[];
  suggestedJobType: string;
  imageUrl: string;
}

export function PhotoRamsUploader({
  onGenerate,
}: {
  onGenerate: (description: string) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [analysing, setAnalysing] = useState(false);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [editedDescription, setEditedDescription] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const MAX_SIZE = 25 * 1024 * 1024;

  const handleFile = useCallback(
    (f: File) => {
      setError(null);
      setResult(null);

      // Permissive check — HEIC/HEIF often report an empty MIME, so fall back
      // to the extension. The server detects the real type and transcodes HEIC.
      if (!isLikelyImageUpload(f)) {
        setError("Please upload a JPEG, PNG, WebP, GIF, AVIF, or HEIC image.");
        return;
      }
      if (f.size > MAX_SIZE) {
        setError("File too large — maximum 25MB.");
        return;
      }

      setFile(f);
      const url = URL.createObjectURL(f);
      setPreview(url);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const f = e.dataTransfer.files[0];
      if (f) handleFile(f);
    },
    [handleFile]
  );

  const analyse = async () => {
    if (!file) return;

    setAnalysing(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("image", file);

      const res = await fetch("/api/demo/photo-to-rams", {
        method: "POST",
        body: formData,
        // Bounded so a stalled upload surfaces as an error rather than a
        // spinner that never stops. Matches project-images.
        signal: AbortSignal.timeout(IMAGE_UPLOAD_TIMEOUT_MS),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Failed to analyse photo");
        return;
      }

      setResult(data);
      setEditedDescription(data.description);
    } catch (err) {
      setError(
        err instanceof DOMException && err.name === "TimeoutError"
          ? IMAGE_UPLOAD_TIMEOUT_MESSAGE
          : "Network error — check your connection and try again"
      );
    } finally {
      setAnalysing(false);
    }
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setEditedDescription("");
    setError(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="rounded-2xl border border-border bg-surface p-6 shadow-lg shadow-amber-600/5">
      {/* Analysing overlay */}
      {analysing && (
        // pointer-events-none: the marketing header sits visibly above this
        // 10-20s scrim and must stay clickable; inner card re-enables pointer
        // events. Same pattern as components/ui/ai-progress-overlay.tsx.
        <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center bg-white/95 backdrop-blur-sm">
          <div className="pointer-events-auto mx-auto flex max-w-md flex-col items-center px-4 text-center">
            <div className="mb-6">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-accent-light">
                <svg
                  className="h-8 w-8 animate-spin text-brand-700"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-foreground">
                Analysing your site photo...
              </h2>
              <p className="mt-2 text-sm text-text-secondary">
                AI is identifying hazards, work activities, and site conditions.
                This takes 10–20 seconds.
              </p>
            </div>
          </div>
        </div>
      )}

      {result ? (
        /* Analysis result */
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            {preview && (
              // eslint-disable-next-line @next/next/no-img-element -- preview is a blob: URL from local user upload; next/image cannot optimise blob URLs
              <img
                src={preview}
                alt="Site photo"
                className="h-20 w-20 shrink-0 rounded-lg object-cover"
              />
            )}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-foreground">
                {result.suggestedJobType}
              </p>
              <div className="mt-1 flex flex-wrap gap-1">
                {result.detectedHazards.map((h) => (
                  <span
                    key={h}
                    className="inline-flex rounded-full bg-danger-bg px-2 py-0.5 text-xs font-medium text-danger"
                  >
                    {h}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label
              htmlFor="photo-desc"
              className="block text-sm font-semibold text-foreground"
            >
              AI-generated description — edit if needed
            </label>
            <textarea
              id="photo-desc"
              value={editedDescription}
              onChange={(e) => setEditedDescription(e.target.value)}
              rows={3}
              maxLength={500}
              className="mt-2 w-full rounded-lg border border-border px-4 py-3 text-sm text-foreground placeholder:text-text-tertiary focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
            />
            <div className="mt-1 flex items-center justify-between">
              <span className="text-xs text-text-tertiary">
                {editedDescription.length}/500 characters
              </span>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => onGenerate(editedDescription)}
              disabled={!editedDescription.trim() || editedDescription.trim().length < 10}
              className="flex-1 rounded-lg bg-accent px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-accent/25 transition-colors hover:bg-accent-dark disabled:opacity-50"
            >
              Generate RAMS from this description →
            </button>
            <button
              type="button"
              onClick={reset}
              className="rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-muted transition-colors hover:bg-surface-muted"
            >
              Try another photo
            </button>
          </div>
        </div>
      ) : preview ? (
        /* Preview with analyse button */
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            {/* eslint-disable-next-line @next/next/no-img-element -- preview is a blob: URL from local user upload; next/image cannot optimise blob URLs */}
            <img
              src={preview}
              alt="Site photo preview"
              className="h-24 w-24 shrink-0 rounded-lg object-cover"
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground">{file?.name}</p>
              <p className="text-xs text-text-secondary">
                {file ? `${(file.size / 1024 / 1024).toFixed(1)} MB` : ""}
              </p>
              <button
                type="button"
                onClick={reset}
                className="mt-1 text-xs text-text-secondary underline hover:text-muted"
              >
                Remove
              </button>
            </div>
          </div>

          <button
            type="button"
            onClick={analyse}
            className="w-full rounded-lg bg-accent px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-accent/25 transition-colors hover:bg-accent-dark"
          >
            Analyse site photo →
          </button>
        </div>
      ) : (
        /* Drop zone */
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          className={`cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition-colors ${
            dragOver
              ? "border-accent bg-accent-light"
              : "border-border hover:border-accent/30 hover:bg-accent-light"
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            accept={IMAGE_UPLOAD_ACCEPT}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
            }}
            className="hidden"
          />
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-accent-light">
            <svg
              className="h-6 w-6 text-brand-700"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316Z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.5 12.75a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0Z"
              />
            </svg>
          </div>
          <p className="text-sm font-semibold text-foreground">
            Upload a site photo
          </p>
          <p className="mt-1 text-xs text-text-secondary">
            Drag &amp; drop or tap to select · JPEG, PNG, WebP, HEIC · Max 10MB
          </p>
        </div>
      )}

      {error && (
        <p className="mt-3 text-sm font-medium text-danger">{error}</p>
      )}
    </div>
  );
}
